import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { api, errMsg, fmtEur2 as fmtEur, fmtDate } from "@/lib/api";
import { EnviarFactura } from "@/components/eco/EnviarFactura";

const Lineas = ({ f }) => (
  <table className="w-full text-sm mt-6">
    <thead><tr className="border-b-2 border-slate-800 text-left text-xs uppercase"><th className="py-1">Concepto</th><th className="py-1 text-right">Base</th><th className="py-1 text-right">IVA</th><th className="py-1 text-right">Importe</th></tr></thead>
    <tbody>
      {f.lineas.map((l, i) => (
        <tr key={i} className="border-b border-slate-200">
          <td className="py-1.5">{l.concepto}</td>
          <td className="py-1.5 text-right">{fmtEur(l.importe)}</td>
          <td className="py-1.5 text-right">{l.iva ? `${l.iva}%` : "Exenta"}</td>
          <td className="py-1.5 text-right">{fmtEur(l.importe * (1 + l.iva / 100))}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function FacturaImprimir() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const docRef = useRef(null);
  const load = () => api.get(`/eco/facturas/${id}`).then((r) => setF(r.data));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const rectificar = async () => {
    const motivo = window.prompt("Motivo de la rectificación (se crea una factura rectificativa en negativo):");
    if (!motivo) return;
    try {
      const { data } = await api.post(`/eco/facturas/${f.id}/rectificar`, { motivo });
      toast.success(`Rectificativa ${data.numero} emitida`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="factura-imprimir">
      <div className="no-print flex gap-2 mb-4">
        <Link to="/economico" className="rounded-md border border-slate-300 px-3 py-2 text-sm">← Económico</Link>
        <div className="flex-1" />
        {f.estado === "emitida" && f.serie === "" && <button onClick={rectificar} data-testid="factura-rectificar" className="rounded-md border border-rose-300 px-3 py-2 text-sm text-rose-700">Rectificar</button>}
        <button onClick={() => window.print()} data-testid="factura-print" className="inline-flex items-center gap-2 rounded-md bg-[#1E3A8A] px-4 py-2 text-sm font-medium text-white"><Printer className="h-4 w-4" />Imprimir / guardar PDF</button>
      </div>
      <EnviarFactura f={f} docRef={docRef} onDone={load} />
      <div ref={docRef} className="mx-auto max-w-3xl bg-white border border-slate-200 p-10 print:border-0 print:p-0 text-slate-900">
        <div className="flex justify-between gap-6">
          <div className="text-xs leading-relaxed"><p className="text-base font-bold">{f.emisor.razon_social}</p><p>CIF: {f.emisor.cif}</p>{(f.emisor.centros || []).map((c) => <p key={c}>{c}</p>)}<p>{f.emisor.email} · {f.emisor.web}</p></div>
          <div className="text-right"><p className="text-2xl font-[Outfit] font-bold">{f.serie === "R" ? "FACTURA RECTIFICATIVA" : "FACTURA"}</p><p className="font-[JetBrains_Mono]" data-testid="factura-numero">Nº {f.numero}</p><p className="text-sm">Fecha: {fmtDate(f.fecha)}</p></div>
        </div>
        <div className="mt-6 rounded border border-slate-300 p-3 text-sm"><p className="text-[10px] uppercase text-slate-500">Cliente</p><p className="font-semibold">{f.cliente_nombre}</p>{f.cliente_nif && <p>NIF: {f.cliente_nif}</p>}{f.cliente_direccion && <p>{f.cliente_direccion}</p>}</div>
        <Lineas f={f} />
        <div className="mt-4 ml-auto w-64 text-sm">
          <p className="flex justify-between"><span>Base imponible</span><span>{fmtEur(f.base)}</span></p>
          <p className="flex justify-between"><span>IVA</span><span>{fmtEur(f.cuota_iva)}</span></p>
          <p className="flex justify-between border-t-2 border-slate-800 mt-1 pt-1 text-base font-bold"><span>TOTAL</span><span data-testid="factura-total-print">{fmtEur(f.total)}</span></p>
        </div>
        {f.notas && <p className="mt-6 text-xs">{f.notas}</p>}
        {f.texto_exenta && <p className="mt-2 text-xs text-slate-600">{f.texto_exenta}</p>}
        {f.estado === "rectificada" && <p className="mt-2 text-xs font-semibold text-rose-700 no-print">Esta factura está rectificada por la {f.rectificada_por}.</p>}
        <p className="mt-10 text-[9px] text-slate-400 break-all">Huella: {f.huella}</p>
      </div>
    </div>
  );
}
